// Type definition for props
interface OptimizedLoginFormProps {
  onSubmit: (email: string, captchaToken: string) => Promise<void>;
  isLoading: boolean;
}

import React, { useState, useRef, useEffect } from 'react';

const OptimizedLoginForm: React.FC<OptimizedLoginFormProps> = ({ onSubmit, isLoading }) => {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [token, setToken] = useState('');
  const widgetId = useRef();
  const containerRef = useRef(null);

  useEffect(() => {
    let scriptLoaded = false;
    
    const loadTurnstile = () => {
      if (containerRef.current && window.turnstile) {
        widgetId.current = window.turnstile.render(containerRef.current, {
          sitekey: '0x4AAAAAAA4OGMUHD1Kc7Rta',
          theme: 'dark',
          callback: function(token) {
            setToken(token);
          },
        });
      }
    };

    if (window.turnstile) {
      loadTurnstile();
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?onload=onloadTurnstileCallback';
    script.async = true;
    script.defer = true;

    window.onloadTurnstileCallback = () => {
      if (!scriptLoaded) {
        scriptLoaded = true;
        loadTurnstile();
      }
    };

    document.head.appendChild(script);

    return () => {
      if (script.parentElement) {
        script.parentElement.removeChild(script);
      }
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!token) {
      alert('Please complete the CAPTCHA');
      return;
    }

    try {
      await onSubmit(email, token);
      setSent(true);
    } catch (error) {
      if (window.turnstile && containerRef.current) {
        window.turnstile.reset(containerRef.current);
      }
      setToken('');
    }
  };

  const SuccessCard = () => (
    <div className="text-center">
      <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-emerald-500 text-slate-900 mb-4">
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
      </div>
      <h3 className="text-xl font-semibold text-slate-100 mb-4">
        Check Your Email
      </h3>
      <p className="text-slate-300 mb-6">
        We've sent a link to your email (check your Spam folder). Click it to start your journey with CHAD.
      </p>
      <button
      onClick={() => {
        // First clear all states
        setSent(false);
        setEmail('');
        setToken('');

        // Force a complete reset of Turnstile
        if (window.turnstile && widgetId.current) {
          window.turnstile.reset(widgetId.current);
          // Remove the old widget entirely
          const oldWidget = containerRef.current?.querySelector('iframe');
          if (oldWidget) {
            oldWidget.remove();
          }

          // Small delay to ensure clean reset before re-rendering
          setTimeout(() => {
            widgetId.current = window.turnstile.render(containerRef.current, {
              sitekey: '0x4AAAAAAA4OGMUHD1Kc7Rta',
              theme: 'dark',
              callback: function(newToken) {
                setToken(newToken);
              },
            });
          }, 100);
        }
      }}
      className="text-emerald-500 hover:text-emerald-400"
    >
      Use a different email
    </button>
  </div>
);

  return (
    <div className="min-h-screen bg-slate-900 overflow-hidden">
      <div className="relative">
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-600/10 via-slate-900/50 to-slate-900" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="min-h-[calc(100vh-280px)] flex items-center">
            <div className="w-full py-12 lg:grid lg:grid-cols-12 lg:gap-12">
              <div className="lg:col-span-7">
                <div className="text-center lg:text-left lg:flex lg:flex-col lg:justify-center">
                  <img src="/android-chrome-192x192.png" alt="CHAD Logo" className="h-16 w-16 mx-auto lg:mx-0 mb-8" />
                  <h1 className="text-4xl font-bold tracking-tight text-slate-100 sm:text-5xl xl:text-6xl mb-6">
                    The World's First <span className="text-emerald-500">Based AI</span>
                  </h1>
                  <p className="mx-auto lg:mx-0 max-w-2xl text-xl text-slate-300 mb-8">
                    No more weak NPC responses. Get straight-to-the-point, uncensored advice on fitness, wealth, and relationships.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start mb-12">
                    <div className="flex items-center gap-2 text-slate-300">
                      <svg className="w-5 h-5 text-emerald-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      Free Tier Available
                    </div>
                    <div className="flex items-center gap-2 text-slate-300">
                      <svg className="w-5 h-5 text-emerald-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      No Credit Card Required
                    </div>
                  </div>
                </div>
              </div>

              <div className="lg:col-span-5">
                <div className="bg-slate-800/70 backdrop-blur border border-slate-700 rounded-2xl shadow-xl p-8">
                  {sent ? (
                    <SuccessCard />
                  ) : (
                    <>
                      <h2 className="text-2xl font-bold text-slate-100 text-center mb-2">
                        Start Your Journey
                      </h2>
                      <p className="text-slate-400 text-center mb-6">
                        Sign up in seconds. No password needed.
                      </p>
                      <form onSubmit={handleSubmit} className="space-y-4">
                        <div>
                          <label htmlFor="email" className="sr-only">Email Address</label>
                          <input
                            type="email"
                            id="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="w-full px-4 py-3 rounded-lg bg-slate-700/50 border border-slate-600 text-slate-100 placeholder-slate-400 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                            placeholder="Enter your email"
                            required
                          />
                        </div>
                        
                        <div className="flex justify-center">
                          <div ref={containerRef} />
                        </div>

                        <button
                          type="submit"
                          disabled={isLoading || !token}
                          className="w-full py-3 px-4 bg-emerald-500 text-slate-900 rounded-lg hover:bg-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 focus:ring-offset-slate-800 disabled:opacity-50 disabled:cursor-not-allowed font-medium transition-colors"
                        >
                          {isLoading ? (
                            <span className="flex items-center justify-center gap-2">
                              <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                              </svg>
                              Sending...
                            </span>
                          ) : (
                            'Get Started Free'
                          )}
                        </button>
                      </form>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="relative border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-3xl font-bold text-slate-100 mb-1">100K+</div>
              <div className="text-sm text-slate-400">Active Users</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-slate-100 mb-1">1M+</div>
              <div className="text-sm text-slate-400">Messages Sent</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-slate-100 mb-1">4.9/5</div>
              <div className="text-sm text-slate-400">User Rating</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-slate-100 mb-1">24/7</div>
              <div className="text-sm text-slate-400">Availability</div>
            </div>
          </div>
        </div>
      </div>

      <footer className="border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center text-sm text-slate-400">
            <div className="space-x-4 mb-4">
              <a href="/privacy.html" className="text-emerald-400 hover:text-emerald-300">Privacy Policy</a>
              <span className="text-slate-600">•</span>
              <a href="/terms.html" className="text-emerald-400 hover:text-emerald-300">Terms of Service</a>
              <span className="text-slate-600">•</span>
              <a href="/aup.html" className="text-emerald-400 hover:text-emerald-300">Acceptable Use</a>
              <span className="text-slate-600">•</span>
              <a href="mailto:email@mentalgymnasticscorp.com" className="text-emerald-400 hover:text-emerald-300">Contact Us</a>
            </div>
            <p>© 2025 Mental Gymnastics Corporation. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default OptimizedLoginForm;
